import {useMemo, useRef} from 'react';
import {useIntl} from 'react-intl';
import {Maximize2, PhoneOff, RefreshCw} from 'lucide-react';
import {useMaybeRoomContext} from '@livekit/components-react';
import {groupCallMessages as gm} from '../../groupCalls/messages';
import {BubbleControl} from '../components/controls';
import {useElementSize} from '../hooks/useElementSize';
import type {GroupCallLeg} from '../model/types';
import {groupActions} from './groupActions';
import {GroupChip} from './GroupBubbleViews';
import {GroupMoreMenu} from './GroupMoreMenu';
import {GroupTile, SelfView} from './GroupTile';
import {fitTiles, useGroupTracks, useStableTiles} from './useGroupTracks';
import type {ControlSpec, GroupControls} from './useGroupControls';
import {groupSurfaceMessages as m} from './groupMessages';

const PIP_TILES = 6;
const PIP_GAP = 6;
const SELF_VIEW = {ratio: 0.28, min: 72, max: 220} as const;

const PipTiles = () => {
    const {cameras, speakerIdentity, speaking} = useGroupTracks();
    const others = useMemo(() => cameras.filter(tile => !tile.view.isLocal), [cameras]);
    const remote = useStableTiles(others, speakerIdentity, PIP_TILES);
    const local = cameras.find(tile => tile.view.isLocal);
    const area = useRef<HTMLDivElement>(null);
    const size = useElementSize(area);
    const shown = remote.length > 0 ? remote : local ? [local] : [];
    const tile = fitTiles(shown.length, size.width, size.height, PIP_GAP);
    const selfWidth = Math.round(Math.min(Math.max(size.width * SELF_VIEW.ratio, SELF_VIEW.min), SELF_VIEW.max));
    return (
        <div ref={area} data-testid="group-call-pip-grid" className="relative flex h-full w-full flex-wrap content-center items-center justify-center gap-1.5 overflow-hidden">
            {tile.width > 0 && shown.map(({trackRef, view}) => (
                <GroupTile
                    key={view.identity}
                    trackRef={trackRef}
                    view={view}
                    variant="pip"
                    speaking={speaking.has(view.identity)}
                    style={{width: tile.width, height: tile.height}}
                    className="shrink-0"
                />
            ))}
            {remote.length > 0 && local && (
                <SelfView
                    trackRef={local.trackRef}
                    speaking={speaking.has(local.view.identity)}
                    style={{width: selfWidth}}
                    className="absolute bottom-1.5 right-1.5 aspect-video"
                />
            )}
        </div>
    );
};

interface GroupPipViewProps {
    readonly leg: GroupCallLeg;
    readonly controls: GroupControls;
    readonly handRaised: boolean;
    readonly onReturnToCall: () => void;
}

export const GroupPipView = ({leg, controls, handRaised, onReturnToCall}: GroupPipViewProps) => {
    const intl = useIntl();
    const room = useMaybeRoomContext();
    const reconnecting = leg.network === 'reconnecting';
    const items = [controls.microphone, controls.camera].filter((item): item is ControlSpec => item !== undefined);
    return (
        <div
            data-testid="group-call-pip"
            className="phq-call-ui flex h-screen flex-col gap-2.5 overflow-hidden p-3 text-[var(--phq-call-ink)] [background-image:var(--phq-call-stage-gradient)]"
        >
            <div className="flex min-h-8 items-center justify-between gap-2">
                <GroupChip leg={leg}/>
                <button
                    type="button"
                    data-testid="group-call-return"
                    onClick={onReturnToCall}
                    className="phq-call-press inline-flex h-8 shrink-0 items-center gap-1.5 rounded-[10px] bg-[var(--phq-call-brand)] px-3 text-[13px] font-semibold text-white"
                >
                    <Maximize2 size={14} aria-hidden="true"/>
                    {intl.formatMessage(m.returnToCall)}
                </button>
            </div>
            <div className={`relative min-h-0 flex-1 ${reconnecting ? 'opacity-[.48]' : ''}`}>
                {room ? <PipTiles/> : null}
                {reconnecting && (
                    <span className="absolute left-1/2 top-1/2 inline-flex h-8 -translate-x-1/2 -translate-y-1/2 items-center gap-1.5 rounded-lg bg-[var(--phq-call-chip-scrim)] px-3 text-[13px] font-semibold">
                        <RefreshCw size={14} className="animate-spin" aria-hidden="true"/>
                        {intl.formatMessage(m.pillReconnecting)}
                    </span>
                )}
            </div>
            <div className="flex items-center justify-center gap-2">
                {items.map(item => (
                    <BubbleControl
                        key={item.id}
                        icon={item.icon}
                        label={item.label}
                        tone={item.tone}
                        alert={item.alert}
                        pressed={item.pressed}
                        disabled={item.disabled || reconnecting}
                        testId={item.testId}
                        onClick={item.onClick}
                    />
                ))}
                <GroupMoreMenu variant="bubble" handRaised={handRaised} disabled={reconnecting}/>
                <button
                    type="button"
                    data-testid="group-call-leave"
                    onClick={groupActions.leave}
                    className="phq-call-press inline-flex h-10 items-center gap-2 rounded-[10px] bg-[var(--phq-call-danger)] px-4 text-sm font-semibold text-white"
                >
                    <PhoneOff size={16} aria-hidden="true"/>
                    {intl.formatMessage(gm.controlLeave)}
                </button>
            </div>
        </div>
    );
};
